import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChangePasswordRequest,
  LoginRequest,
  PasswordResetRequest,
  RegisterRequest,
} from '@financial/shared';
import { useAuthStore } from '../../store/auth';
import {
  changePasswordRequest,
  confirmEmailVerificationRequest,
  fetchPrivacyNotice,
  loginRequest,
  logoutRequest,
  registerRequest,
  resendEmailVerificationRequest,
  requestPasswordResetRequest,
  resetPasswordRequest,
  validatePasswordResetRequest,
} from './api';

/** Hooks de autenticación (login, registro, logout) enlazados al store. */
export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginRequest) => loginRequest(body),
    // La caché puede contener datos y conversaciones de la sesión previa.
    // Nunca debe sobrevivir a un cambio de identidad en el mismo navegador.
    onSuccess: (data) => {
      queryClient.clear();
      setSession(data);
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (body: RegisterRequest) => registerRequest(body),
  });
}

export function useConfirmEmailVerification() {
  const setSession = useAuthStore((s) => s.setSession);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: confirmEmailVerificationRequest,
    onSuccess: (data) => {
      queryClient.clear();
      setSession(data);
    },
  });
}

export function useResendEmailVerification() {
  return useMutation({ mutationFn: resendEmailVerificationRequest });
}

export function usePrivacyNotice() {
  return useQuery({
    queryKey: ['privacy-notice'],
    queryFn: fetchPrivacyNotice,
    staleTime: Infinity,
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: (body: ChangePasswordRequest) => changePasswordRequest(body) });
}

export function usePasswordResetValidation(token: string | null) {
  return useQuery({
    queryKey: ['password-reset', token],
    queryFn: () => validatePasswordResetRequest({ token: token ?? '' }),
    enabled: Boolean(token),
    retry: false,
  });
}

export function usePasswordReset() {
  return useMutation({ mutationFn: (body: PasswordResetRequest) => resetPasswordRequest(body) });
}

export function useRequestPasswordReset() {
  return useMutation({ mutationFn: requestPasswordResetRequest });
}

export function useLogout() {
  const clear = useAuthStore((s) => s.clear);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await logoutRequest();
      } catch {
        /* aunque falle el server, limpiamos localmente */
      }
    },
    onSettled: () => {
      queryClient.clear();
      clear();
    },
  });
}
